import { createContext, useContext, useEffect, type ComponentType } from 'react'

export interface BreadcrumbItem {
	label: string
	path?: string
}

/**
 * Rendered at the trailing (right) edge of the breadcrumb row — a page-level
 * status chip such as the itinerary's live-updates badge. Registered as a
 * component type rather than an element so its identity is stable across the
 * page's renders and the effect that publishes it never loops.
 */
export type BreadcrumbTrailing = ComponentType

interface BreadcrumbContextValue {
	breadcrumbs: BreadcrumbItem[]
	setBreadcrumbs: (items: BreadcrumbItem[]) => void
	trailing: BreadcrumbTrailing | null
	setTrailing: (component: BreadcrumbTrailing | null) => void
}

export const BreadcrumbContext = createContext<BreadcrumbContextValue | undefined>(undefined)

export const useBreadcrumbContext = () => {
	const context = useContext(BreadcrumbContext)
	if (!context) {
		throw new Error('useBreadcrumbContext must be used within BreadcrumbProvider')
	}
	return context
}

/**
 * Hook to set breadcrumbs for a page, plus an optional trailing component for
 * the right edge of the row. Automatically cleans up both on unmount.
 *
 * Trails start at the trip name (the overview is the root and shows no trail).
 *
 * @example
 * useBreadcrumbs(
 *   [{ label: trip.name, path: `/trips/${tripId}` }, { label: 'Itinerary' }],
 *   RealtimeStatusBadge,
 * )
 */
export const useBreadcrumbs = (items: BreadcrumbItem[], trailing?: BreadcrumbTrailing) => {
	const { setBreadcrumbs, setTrailing } = useBreadcrumbContext()
	const serializedItems = JSON.stringify(items)

	useEffect(() => {
		setBreadcrumbs(JSON.parse(serializedItems) as BreadcrumbItem[])

		// Cleanup: clear breadcrumbs when component unmounts
		return () => {
			setBreadcrumbs([])
		}
	}, [serializedItems, setBreadcrumbs])

	useEffect(() => {
		setTrailing(trailing ?? null)

		return () => {
			setTrailing(null)
		}
	}, [trailing, setTrailing])
}
